// src/components/admin/Customers.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSearch, FaSync, FaUsers, FaSyncAlt, FaMoneyBillWave, FaUserPlus } from 'react-icons/fa';
import './Customers.css';

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    total_customers: 0,
    repeat_customers: 0,
    total_spend: 0,
    new_this_month: 0
  });
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      // TODO: this endpoint (get-customers.php) is new — see the file I
      // provided alongside this component. It aggregates the `orders`
      // table since there's no dedicated customers table yet.
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/get-customers.php');
      const data = await response.json();
      if (data.success) {
        setCustomers(data.customers || []);
        setStats(data.stats || stats);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = (type) => {
    const labels = {
      new: 'New Customer',
      repeat: 'Repeat Customer',
      high_value: 'High Value Customer'
    };
    return labels[type] || 'Customer';
  };

  const typeClass = (type) => {
    const classes = {
      new: 'type-new',
      repeat: 'type-repeat',
      high_value: 'type-high-value'
    };
    return classes[type] || 'type-new';
  };

  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.floor((today.setHours(0, 0, 0, 0) - new Date(date).setHours(0, 0, 0, 0)) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return 'Last week';
    return date.toLocaleDateString();
  };

  const statCards = [
    { title: 'Total Customers', value: stats.total_customers, sub: 'Customers who ordered', icon: <FaUsers />, bg: '#fdece8', color: '#e8604c' },
    { title: 'Repeat Customers', value: stats.repeat_customers, sub: 'Ordered more than once', icon: <FaSyncAlt />, bg: '#dcf5e3', color: '#1e8a4c' },
    { title: 'Total Customer Spend', value: `PKR ${Number(stats.total_spend).toLocaleString()}`, sub: 'From completed orders', icon: <FaMoneyBillWave />, bg: '#dbeafe', color: '#1d4ed8' },
    { title: 'New This Month', value: stats.new_this_month, sub: 'Recently added', icon: <FaUserPlus />, bg: '#fdf1d6', color: '#a3760a' },
  ];

  const filteredCustomers = useMemo(() => {
    return customers.filter(c => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !term ||
        (c.name || '').toLowerCase().includes(term) ||
        (c.phone || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term);

      const matchesType = typeFilter === 'all' || c.type === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [customers, searchTerm, typeFilter]);

  const handleResetFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
  };

  // Reuses the existing (real) Orders page search — no new customer-detail
  // page/API was built for this. See note in Orders.jsx re: reading ?search=
  const handleViewOrders = (customer) => {
    navigate(`/admin/orders?search=${encodeURIComponent(customer.email)}`);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="customers-page">
      <div className="customers-header">
        <h1>Customers</h1>
        <p>View your store customers, order history, and basic customer details.</p>
        <p className="customers-note">Customer data is created automatically when someone places an order.</p>
      </div>

      {/* STAT CARDS */}
      <div className="customers-stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="customers-stat-card">
            <div className="customers-stat-top">
              <div className="customers-stat-icon" style={{ background: stat.bg, color: stat.color }}>
                {stat.icon}
              </div>
              <p className="customers-stat-title">{stat.title}</p>              
            </div>
            <h3 className="customers-stat-value">{stat.value}</h3>
            <p className="customers-stat-sub">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* SEARCH + FILTER */}
      <div className="customers-filter-bar">
        <div className="customers-search">
          <FaSearch className="customers-search-icon" />
          <input
            type="text"
            placeholder="Search by customer name, phone number, or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="customers-filter-select">
          <option value="all">All Customers</option>
          <option value="new">New Customer</option>
          <option value="repeat">Repeat Customer</option>
          <option value="high_value">High Value Customer</option>
        </select>

        <button className="reset-filters-btn" onClick={handleResetFilters}>
          <FaSync /> Reset
        </button>
      </div>

      {/* TABLE */}
      {filteredCustomers.length === 0 ? (
        <div className="no-customers">
          <p>No customers found.</p>
        </div>
      ) : (
        <div className="customers-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Orders</th>
                <th>Spent</th>
                <th>Last Order</th>
                <th>Type</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer, index) => (
                <tr key={customer.email || index}>
                  <td className="customer-name-cell">{customer.name}</td>
                  <td>{customer.phone || '—'}</td>
                  <td className="muted-cell">{customer.email}</td>
                  <td>{customer.order_count}</td>
                  <td>PKR {Number(customer.total_spent).toLocaleString()}</td>
                  <td className="muted-cell">{formatRelativeDate(customer.last_order_at)}</td>
                  <td>
                    <span className={`type-pill ${typeClass(customer.type)}`}>
                      {typeLabel(customer.type)}
                    </span>
                  </td>
                  <td>
                    <button className="view-customer-btn" onClick={() => handleViewOrders(customer)}>
                      <FaEye /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Customers;
